import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { ChemicalTemplate } from '../data-models/chemical-template.model';
import { ChemicalTemplateRequest } from '../request-models/chemical-template-request.model';
import { TemplateChemicalCopy } from '../response-models/template-chemical-copy.model';
import { toChemicalTemplateRequest } from '../utils/chemical-template-request-utils';
import { isRecord } from '../utils/is-record';
import { nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * Reusable chemical mixes of the current organization: `/chemical-templates[/:id[/copy]]`
 * (hub `user-chemical-templates-controller.ts`). The hub scopes every route to the organization the auth
 * interceptor sends. The list is not paged and includes each template's items.
 *
 * Holds the loaded templates as signal state. Failures set `error` (the hub's message) and resolve to
 * `null` / `false` rather than rejecting.
 */
@Injectable({
  providedIn: 'root'
})
export class ChemicalTemplateService extends BaseService<ChemicalTemplate> {
  // Signal-based state management
  private templatesSignal = signal<ChemicalTemplate[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals
  readonly templates = computed(() => this.templatesSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Sorted by usage count (most used first)
  readonly popularTemplates = computed(() =>
    [...this.templatesSignal()].sort((a, b) => b.usageCount - a.usageCount)
  );

  constructor() {
    super('chemical-templates', ChemicalTemplate);
  }

  /**
   * Load chemical templates for current organization
   * GET /chemical-templates?search=
   */
  async loadTemplates(options: { search?: string } = {}): Promise<void> {
    const query = { search: options.search || undefined };
    const templates = await this.track('Failed to load chemical templates', () => firstValueFrom(this.getMany({ query })));
    if (templates) {
      this.templatesSignal.set(templates);
    }
  }

  /**
   * Get chemical template by ID (with all items)
   */
  async getTemplateById(id: string): Promise<ChemicalTemplate | null> {
    return this.track('Failed to fetch chemical template', () => firstValueFrom(this.get(id)));
  }

  /**
   * Create a new chemical template with items
   */
  async createTemplate(dto: ChemicalTemplateRequest): Promise<ChemicalTemplate | null> {
    const created = await this.track('Failed to create chemical template', () =>
      firstValueFrom(this.create(toChemicalTemplateRequest(dto)))
    );
    if (created) {
      this.templatesSignal.update(templates => [...templates, created]);
    }
    return created;
  }

  /**
   * Update an existing chemical template. `chemicals`, when sent, replaces all its items.
   */
  async updateTemplate(id: string, dto: ChemicalTemplateRequest): Promise<ChemicalTemplate | null> {
    const saved = await this.track('Failed to update chemical template', () =>
      firstValueFrom(this.update(id, toChemicalTemplateRequest(dto)))
    );
    if (saved) {
      this.templatesSignal.update(templates => templates.map(t => (t.id === id ? saved : t)));
    }
    return saved;
  }

  /**
   * Delete a chemical template (soft-delete)
   */
  async deleteTemplate(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete chemical template', async () => {
      await firstValueFrom(this.delete(id));
      return true;
    });
    if (deleted) {
      this.templatesSignal.update(templates => templates.filter(t => t.id !== id));
    }
    return deleted ?? false;
  }

  /**
   * Copy a template to a spray record: the hub formats its chemicals for the form and counts the use.
   * POST /chemical-templates/:id/copy
   */
  async copyToSprayRecord(id: string): Promise<TemplateChemicalCopy[] | null> {
    const chemicals = await this.track('Failed to copy template', () =>
      firstValueFrom(this.request('POST', { id, path: 'copy' }, toTemplateChemicalCopies, {}))
    );
    if (chemicals) {
      this.templatesSignal.update(templates => templates.map(t => {
        if (t.id === id) {
          t.usageCount += 1;
        }
        return t;
      }));
    }
    return chemicals;
  }

  /**
   * Search templates (client-side filtering)
   */
  searchTemplates(query: string): ChemicalTemplate[] {
    if (!query || query.trim().length < 2) {
      return this.templatesSignal();
    }

    const searchTerm = query.toLowerCase().trim();
    return this.templatesSignal().filter(t =>
      t.name?.toLowerCase().includes(searchTerm) ||
      t.description?.toLowerCase().includes(searchTerm) ||
      t.targetCrops?.some(c => c.toLowerCase().includes(searchTerm)) ||
      t.targetPests?.some(p => p.toLowerCase().includes(searchTerm))
    );
  }

  /**
   * Get templates by target crop (client-side filtering)
   */
  getTemplatesByCrop(crop: string): ChemicalTemplate[] {
    return this.templatesSignal().filter(t =>
      t.targetCrops?.some(c => c.toLowerCase() === crop.toLowerCase())
    );
  }

  /**
   * Get templates by target pest (client-side filtering)
   */
  getTemplatesByPest(pest: string): ChemicalTemplate[] {
    return this.templatesSignal().filter(t =>
      t.targetPests?.some(p => p.toLowerCase() === pest.toLowerCase())
    );
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Runs an action with the loading flag set. A failure records the hub's message (or `fallback`) in `error`
   * and resolves to `null`.
   */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? fallback;
      console.error(`[ChemicalTemplateService] ${fallback}:`, message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }
}

function toTemplateChemicalCopies(data: unknown): TemplateChemicalCopy[] {
  return Array.isArray(data) ? data.filter(isRecord).map(toTemplateChemicalCopy) : [];
}

function toTemplateChemicalCopy(source: Record<string, unknown>): TemplateChemicalCopy {
  return {
    chemicalId: nullableString(source['chemicalId']) ?? undefined,
    customChemicalId: nullableString(source['customChemicalId']) ?? undefined,
    name: nullableString(source['name']) ?? '',
    epaNumber: nullableString(source['epaNumber']) ?? undefined,
    rate: nullableString(source['rate']) ?? '',
    rateValue: nullableNumber(source['rateValue']),
    rateUnit: nullableString(source['rateUnit']),
    ratePerUnit: nullableString(source['ratePerUnit']),
    source: 'template',
    templateName: nullableString(source['templateName']) ?? '',
    purpose: nullableString(source['purpose']) ?? undefined,
  };
}
